import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Search } from "lucide-react";
import KennelSEO from "./KennelSEO";
import { articles, categories } from "./seoArticlesData";
import PageHero from "./PageHero";

const KennelKnowledge = () => {
  useOutletContext();
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = articles.filter(a => {
    const matchCat = activeCategory === "all" || a.category === activeCategory;
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div>
      <KennelSEO
        title="Wiedza o Owczarku Niemieckim | Wolfe vom Katzenberg"
        description="Kompendium wiedzy o owczarku niemieckim — artykuly o charakterze, zdrowiu, badaniach, hodowli i wystawach. Porady od doswiadczonego hodowcy."
        canonical="https://wolfevomkatzenberg.pl/wiedza-o-rasie"
      />

      <PageHero
        breadcrumbs={[{ label: "WIEDZA O RASIE" }]}
        label="WIEDZA O RASIE"
        title="Owczarek niemiecki — pies wyjątkowy"
        description="Rasa pełna charakteru, inteligencji i siły. Tu znajdziesz rzetelną wiedzę o owczarku niemieckim — od budowy i zdrowia po charakter i wychowanie. Wszystko oparte na naszym doświadczeniu hodowlanym i codziennej pracy z tymi psami."
        testId="knowledge"
      />

      <div className="px-6 md:px-12 py-10 max-w-7xl mx-auto">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-12 items-start md:items-center justify-between" data-testid="knowledge-filters">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 text-xs tracking-wider border transition-all duration-300 ${activeCategory === "all" ? "bg-[#D4AF37] text-[#0B0B0D] border-[#D4AF37]" : "border-[#2A2A2D] text-[#B8B8B8]/50 hover:border-[#D4AF37]/30"}`}
              data-testid="filter-all"
            >WSZYSTKIE</button>
            {Object.entries(categories).map(([key, label]) => (
              <button key={key} onClick={() => setActiveCategory(key)}
                className={`px-4 py-2 text-xs tracking-wider border transition-all duration-300 ${activeCategory === key ? "bg-[#D4AF37] text-[#0B0B0D] border-[#D4AF37]" : "border-[#2A2A2D] text-[#B8B8B8]/50 hover:border-[#D4AF37]/30"}`}
                data-testid={`filter-${key}`}
              >{label.toUpperCase()}</button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/30" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Szukaj artykułu..."
              className="w-full bg-[#1A1A1D] border border-[#2A2A2D] text-[#F5F5F5] text-sm pl-10 pr-4 py-2.5 focus:border-[#D4AF37]/40 focus:outline-none transition-colors"
              data-testid="knowledge-search"
            />
          </div>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
          {filtered.map((article, idx) => (
            <motion.div key={article.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, duration: 0.4 }}
            >
              <Link to={`/wiedza-o-rasie/${article.slug}`}
                className="block bg-[#1A1A1D] border border-[#2A2A2D] hover:border-[#D4AF37]/20 p-6 transition-all duration-500 group h-full"
                data-testid={`article-card-${article.slug}`}
              >
                <span className="text-[9px] tracking-[0.2em] text-[#D4AF37]/60 uppercase block mb-3">
                  {categories[article.category] || article.category}
                </span>
                <h3 className="font-serif text-[#F5F5F5] group-hover:text-[#D4AF37] transition-colors text-lg leading-tight mb-3">
                  {article.title}
                </h3>
                <p className="text-[#B8B8B8]/40 text-sm line-clamp-3 mb-4 leading-relaxed">
                  {article.content.substring(0, 150)}...
                </p>
                <span className="text-xs tracking-wider text-[#D4AF37]/50 group-hover:text-[#D4AF37] transition-colors flex items-center gap-2">
                  CZYTAJ WIĘCEJ <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="w-8 h-8 text-[#D4AF37]/20 mx-auto mb-4" />
            <p className="text-[#B8B8B8]/40 text-sm">Nie znaleziono artykułów</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KennelKnowledge;
